import { isLatLngLiteral } from '@googlemaps/typescript-guards';
import { Box, type SxProps } from '@mui/material';
import { type TypeEqualityComparator, createCustomEqual, deepEqual } from 'fast-equals';
import {
  Children,
  type EffectCallback,
  type FC,
  type ReactNode,
  cloneElement,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { hasValue } from '@core/utils/equality';
import { tenantCountry } from '@core/utils/formikHelpers';
import { mapViewFor } from '@core/utils/subdivisions';

import { SlabConfig } from '../../utils/SlabConfig';

export const MAP_ZOOM_CLOSE: number = 11;
export const MAP_ZOOM_MID: number = 9;
export const MAP_ZOOM_FAR: number = 4;

/**
 * These custom deep equality components are needed so that the map object can optimize its
 * loading hooks by allowing near equal latitudes and longitudes to act as functionally equal
 * for caching purposes.
 */
const deepEqualForGMaps: TypeEqualityComparator<
  google.maps.Map[keyof google.maps.Map],
  undefined
> = (a, b): boolean => {
  if (
    (isLatLngLiteral(a) || a instanceof google.maps.LatLng) &&
    (isLatLngLiteral(b) || b instanceof google.maps.LatLng)
  ) {
    return new google.maps.LatLng(a).equals(new google.maps.LatLng(b));
  }

  return deepEqual(a, b);
};

const deepCompareEqualsForMaps = createCustomEqual({
  createCustomConfig: () => ({ areObjectsEqual: deepEqualForGMaps }),
});

const useDeepCompareMemoize = (value: HTMLDivElement | null): HTMLDivElement | null => {
  const ref = useRef<HTMLDivElement | null>(null);

  if (!deepCompareEqualsForMaps(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
};

export const UseDeepCompareEffectForMaps = (callback: EffectCallback, dependencies: any[]): void =>
  useEffect(callback, dependencies.map(useDeepCompareMemoize));
/** End of custom equality components */

/**
 * HasMap is an interface describing any components that are a child of a Map
 * component and have a map property.
 */
export interface HasMap {
  map: google.maps.Map;
}

/**
 * MapProps is an extention of the normal Properties passed to a map object
 * with properties used to control the embedded custom behavior of this map.
 */
interface SlabMapProps extends google.maps.MapOptions {
  onClick?(e: google.maps.MapMouseEvent): void;
  onIdle?(map: google.maps.Map): void;
  onIdleOnce?(map: google.maps.Map): void;
  children?: ReactNode;
  sx?: SxProps;
}

/**
 * Map is the React component that houses the map.
 *
 * The returned component is a fragment that contains a div element and the
 * Map's children. Because of how dom is loaded in react and how the Google
 * Maps React wrapper library is set up, the map must be inserted into the
 * div element via the div's ref property.
 */
export const SlabMap: FC<SlabMapProps> = ({
  onClick,
  onIdle,
  onIdleOnce,
  children,
  zoom,
  sx,
  ...options
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);

  // Fallback view when nothing has been geocoded yet. Opening every map on the
  // US is useless for a tenant on the other side of the world.
  const { center: defaultCenter, zoom: defaultZoom } = mapViewFor(tenantCountry());

  // React doesn't use deep comparisons, so it needs a custom hook
  // see https://github.com/googlemaps/js-samples/issues/946
  UseDeepCompareEffectForMaps(() => {
    if (ref.current !== null && map === null) {
      const newMap = new window.google.maps.Map(ref.current, {
        zoom,
        mapId: SlabConfig.googleMaps.id,
      });
      setMap(newMap);
    }
    map?.setOptions({ ...options, center: options.center ?? defaultCenter });
  }, [ref, map]);

  UseDeepCompareEffectForMaps(() => {
    map?.setOptions({ ...options, center: map?.getCenter() });
    if (options.center !== undefined && options.center !== null) {
      map?.panTo(options.center);
    } else {
      map?.panTo(defaultCenter);
      map?.setZoom(defaultZoom);
    }
  }, [map, options]);

  UseDeepCompareEffectForMaps(() => {
    if (hasValue(map) && hasValue(zoom)) {
      map.setZoom(zoom);
    }
  }, [zoom]);

  UseDeepCompareEffectForMaps(() => {
    if (hasValue(onClick)) {
      map?.addListener('click', onClick);
    }

    if (hasValue(onIdle)) {
      map?.addListener('idle', () => onIdle(map));
    }
    if (hasValue(onIdleOnce) && hasValue(map)) {
      google.maps.event.addListenerOnce(map, 'idle', () => onIdleOnce(map));
    }
  }, [map]);

  // Memoize sx prop to avoid expensive type inference on every render
  const boxSx = useMemo(() => ({ minHeight: '30rem', ...sx }), [sx]);

  return (
    <>
      <Box id='map' ref={ref} sx={boxSx} />

      {Children.map(children, (child) => {
        if (isValidElement<HasMap>(child) && map !== null) {
          return cloneElement<HasMap>(child, { map });
        }
        return undefined;
      })}
    </>
  );
};
